import React, { useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  Box,
  ButtonIcon,
  Chip,
  Icon,
  InlineMessage,
  MenuInput,
} from '@saviynt/design-system';
import classnames from 'classnames';

import useGetRuleMetadata from '../../hooks/use-getrulesmetadata';
import { type Attribute, type Condition } from '../../services/types';
import messages from '../../shared/messages';
import { isEmpty } from '../../utils';
import { OPERATORS_WITH_NO_VALIDATION } from '../../utils/constants';
import { useInvertedLocalize, useLocalize } from '../../utils/localization';

import invertedMessages from './messages';

import './RuleCondition.css';

const MAX_ALLOWED_VALUES = 25;

interface RuleConditionProps {
  ruleCondition: Condition[];
  setUpdatedCondition: Dispatch<SetStateAction<Condition>>;
  setIsRuleConfirmed: Dispatch<SetStateAction<boolean>>;
  setConditionValue: Dispatch<SetStateAction<string>>;
}

function RuleCondition({
  // rule,
  ruleCondition,
  setUpdatedCondition,
  setIsRuleConfirmed,
  setConditionValue,
}: RuleConditionProps) {
  const [condition, setCondition] = useState<Condition>(ruleCondition[0]);
  const [inputValue, setInputValue] = useState<string>('');
  const [chips, setChips] = useState<string[]>([]);
  const [selectedAttribute, setSelectedAttribute] = useState<string | null>(
    null
  );
  const [selectedOperator, setSelectedOperator] = useState<string | null>(null);

  const { data } = useGetRuleMetadata();

  const [attributes, setAttributes] = useState<Attribute[]>();

  const localize = useLocalize();
  const l = useInvertedLocalize(invertedMessages);

  useEffect(() => {
    if (data?.entity.users) {
      setAttributes(data.entity.users);
    }
  }, [data]);

  useEffect(() => {
    setCondition(ruleCondition[0]);
  }, [ruleCondition]);

  useEffect(() => {
    if (condition) {
      setSelectedAttribute(condition.attribute);
      setSelectedOperator(condition.operator);
      setChips(condition.values);
    }
  }, [condition, attributes]);

  // Handle Attribute selection change
  const handleAttributesChange = (selectedOption: {
    label: string;
    value: string;
  }) => {
    setUpdatedCondition((prevCondition) => {
      const ruleDescription = Array.from(
        `If users ${selectedOption.label} ${
          prevCondition.operator
        } ${prevCondition.values.join(', ')}`
      )
        .slice(0, 255)
        .join('');

      return {
        ...prevCondition,
        ruleDescription,
        attribute: selectedOption.value,
        attributeLabel: selectedOption.label,
      };
    });
    setSelectedAttribute(selectedOption.value);
    setIsRuleConfirmed(false);
  };

  // Handle operator selection change
  const handleOperatorsChange = (selectedOption: { value: string }) => {
    setUpdatedCondition((prevCondition) => {
      const ruleDescription = Array.from(
        `If users ${prevCondition.attributeLabel ?? prevCondition.attribute} ${
          selectedOption.value
        } ${prevCondition.values.join(', ')}`
      )
        .slice(0, 255)
        .join('');

      return {
        ...prevCondition,
        ruleDescription,
        operator: selectedOption.value,
      };
    });
    setSelectedOperator(selectedOption.value);
    setIsRuleConfirmed(false);
  };

  // Handle input field change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    setIsRuleConfirmed(false);
  };

  useEffect(() => {
    setUpdatedCondition((prevCondition) => {
      const ruleDescription = Array.from(
        `If users ${prevCondition.attributeLabel ?? prevCondition.attribute} ${
          prevCondition.operator
        } ${chips.join(', ')}`
      )
        .slice(0, 255)
        .join('');

      return {
        ...prevCondition,
        ruleDescription,
        values: chips,
      };
    });
  }, [chips, setUpdatedCondition]);

  useEffect(() => {
    setConditionValue(inputValue);
  }, [inputValue, setConditionValue]);

  const addChip = () => {
    if (!inputValue.trim()) {
      return;
    } // Prevent adding empty input

    const newChips = inputValue
      .split(',')
      .map((chip) => chip.trim()) // Trim spaces around each chip
      .filter((chip) => chip !== '' && !chips.includes(chip)); // Remove empty & duplicate chips

    if (
      newChips.length > 0 &&
      chips.length + newChips.length <= MAX_ALLOWED_VALUES
    ) {
      setChips((prevChips) => [...prevChips, ...newChips]);
      setInputValue('');
      setConditionValue('');
    }
  };

  // Handle keydown event (for adding chips on Enter)
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && inputValue.trim() !== '') {
      event.preventDefault();
      addChip();
    } else if (
      (event.key === 'Backspace' || event.key === 'Delete') &&
      inputValue === '' &&
      chips.length > 0
    ) {
      // Remove the last chip when backspace is pressed in an empty input
      const updatedChips = chips.slice(0, -1);

      setChips(updatedChips);
    }
  };

  const handleBlur = () => {
    addChip();
  };

  // Handle chip removal
  const handleChipRemove = (indexToRemove: number) => {
    setChips((prevChips) =>
      prevChips.filter((_, index) => index !== indexToRemove)
    );
    setIsRuleConfirmed(false);
  };

  const showValueInputField =
    !isEmpty(selectedOperator) &&
    !isEmpty(selectedAttribute) &&
    selectedOperator !== null &&
    !OPERATORS_WITH_NO_VALIDATION.includes(selectedOperator);

  return (
    <div>
      {data && (
        <Box className='Ext-RuleCondition'>
          <Box className='Ext-RuleCondition-container'>
            <MenuInput
              options={
                attributes?.map((user) => ({
                  label: localize({
                    id: user.label,
                    defaultMessage: messages[user.label],
                  }),
                  value: user.name,
                  isChecked: user.name === selectedAttribute,
                })) ?? []
              }
              onChange={handleAttributesChange}
              placeholder='Select an attribute'
              menuItemHeightConfigs={{ maxHeight: '10rem', height: '4rem' }}
              isPlacementLocked
              className={classnames(
                'Ext-RuleCondition-container-attributesDropdown'
              )}
            />
            {!isEmpty(selectedAttribute) && (
              <MenuInput
                options={data.operators.map((operator) => ({
                  label: operator,
                  value: operator,
                  isChecked: operator === selectedOperator,
                }))}
                menuItemHeightConfigs={{ height: '2rem', maxHeight: '10rem' }}
                isPlacementLocked
                onChange={handleOperatorsChange}
                placeholder='Select a condition'
                className={classnames(
                  'Ext-RuleCondition-container-conditionsDropdown'
                )}
              />
            )}
            {showValueInputField && (
              <Box className='Ext-RuleCondition-container-inputBoxWrapper'>
                <Box
                  className={classnames(
                    'Ext-RuleCondition-container-inputBoxWrapper-inputBox'
                  )}>
                  {chips.map((chip, index) => (
                    <Box
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${chip}-${index}`}
                      className='Ext-RuleCondition-container-inputBoxWrapper-inputBox-chip'>
                      <Chip
                        key={chip}
                        size='medium'
                        label={chip}
                        kind='outlined'
                        rightIcon={
                          <ButtonIcon
                            kind='ghost'
                            size='xxxSmall'
                            icon={
                              <Icon
                                className='Ext-RuleCondition-container-inputBoxWrapper-inputBox-chipDelete'
                                kind='close'
                                size='xxxSmall'
                              />
                            }
                            onClick={() => handleChipRemove(index)}
                          />
                        }
                      />
                    </Box>
                  ))}
                  <input
                    type='text'
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    placeholder={l(
                      'Enter values separated by commas or press Enter after each value'
                    )}
                    className='Ext-RuleCondition-container-inputBoxWrapper-inputField'
                    disabled={chips.length >= MAX_ALLOWED_VALUES}
                  />
                </Box>
                {chips.length >= MAX_ALLOWED_VALUES && (
                  <InlineMessage
                    text='Not more than 25 are allowed'
                    colorTheme='info'
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </div>
  );
}

export default RuleCondition;
